/**
 * Polymarket-aligned exclusive odds — pure math (no Vue / alias imports).
 * Display only: binary No = 1 − Yes; N-way and NegRisk sibling Yes sum to 1.
 */

export const EXCLUSIVE_EPSILON = 0.02;
export const DEFAULT_HALF = 0.5;

/** True when exclusive-set probs overflow 1+ε (the ~200% / N×50% bug). */
export function exclusiveSumOverflow(sum) {
  const n = Number(sum);
  return Number.isFinite(n) && n > 1 + EXCLUSIVE_EPSILON;
}

export function clamp01(p) {
  const n = Number(p);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

export function toProb(price) {
  const n = Number(price);
  if (!Number.isFinite(n)) return NaN;
  if (n > 1 && n <= 100.5) return clamp01(n / 100);
  return clamp01(n);
}

export function looksLikeDefaultHalf(p) {
  return Math.abs(Number(p) - DEFAULT_HALF) < 1e-9;
}

export function isSpreadOrTotalPair(labels) {
  if (!Array.isArray(labels) || labels.length !== 2) return false;
  const a = String(labels[0] ?? '')
    .trim()
    .toUpperCase();
  const b = String(labels[1] ?? '')
    .trim()
    .toUpperCase();
  return (a === 'OVER' && b === 'UNDER') || (a === 'UNDER' && b === 'OVER');
}

/**
 * Cascading strike markets (e.g. MSFT close above ___): multiple Yes can be true.
 * Must not exclusive-normalize sibling Yes to 100%.
 */
export function isNestedThresholdQuestion(question) {
  const q = String(question ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!q) return false;
  const needles = [
    '高于',
    '以上',
    '___',
    '__',
    'above',
    'higher than',
    'greater than',
    'close above',
    'closing price',
    '收盘价会高于',
    '收盘价是',
    '将达到',
    '达到多少',
  ];
  return needles.some((n) => q.includes(n));
}

export function exclusiveQuestionStem(question, groupItemTitle) {
  const q = String(question ?? '')
    .trim()
    .replace(/\s+/g, ' ');
  const g = String(groupItemTitle ?? '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!q) return g.toLowerCase();
  if (g) {
    const i = q.toLowerCase().indexOf(g.toLowerCase());
    if (i >= 0) {
      const stem = `${q.slice(0, i)}${q.slice(i + g.length)}`.replace(/\s+/g, ' ').trim();
      return (stem || '_').toLowerCase();
    }
  }
  return q.toLowerCase();
}

/**
 * @param {number[]} probs 0–1
 * @param {boolean[]} [anchored]
 * @returns {number[]}
 */
export function redistributeExclusive(probs, anchored) {
  const list = Array.isArray(probs) ? probs.map(clamp01) : [];
  const n = list.length;
  if (n === 0) return list;
  const flags =
    Array.isArray(anchored) && anchored.length === n ? anchored.map(Boolean) : list.map(() => false);

  let sumA = 0;
  const floatIdx = [];
  for (let i = 0; i < n; i += 1) {
    if (flags[i]) sumA += list[i];
    else floatIdx.push(i);
  }

  const scaleToOne = (vals) => {
    const out = vals.map(clamp01);
    const sum = out.reduce((s, p) => s + p, 0);
    if (sum <= 0) return out.map(() => 1 / out.length);
    if (Math.abs(sum - 1) <= EXCLUSIVE_EPSILON) return out;
    return out.map((p) => p / sum);
  };

  if (floatIdx.length === 0) return scaleToOne(list);
  if (sumA >= 1) {
    const out = list.slice();
    for (const i of floatIdx) out[i] = 0;
    return scaleToOne(out);
  }

  const rem = 1 - sumA;
  const sumF = floatIdx.reduce((s, i) => s + list[i], 0);
  const out = list.slice();
  if (sumF <= 0) {
    const share = rem / floatIdx.length;
    for (const i of floatIdx) out[i] = share;
    return out;
  }
  for (const i of floatIdx) out[i] = list[i] * (rem / sumF);
  return out;
}

export function complementBinaryYes(yesProb) {
  const yes = clamp01(yesProb);
  return { yes, no: clamp01(1 - yes) };
}

/** Live two-sided book → implied probability (mid). Else fallback 0–1. */
export function impliedProbFromLiveBook(live, fallbackProb) {
  const bid = Number(live?.bidCents);
  const ask = Number(live?.askCents);
  if (Number.isFinite(bid) && Number.isFinite(ask) && bid > 0 && ask > 0 && bid < ask) {
    return clamp01((bid + ask) / 2 / 100);
  }
  const fb = toProb(fallbackProb);
  return Number.isFinite(fb) ? fb : DEFAULT_HALF;
}

/** Buy ¢: live best ask when present, else implied ¢. */
export function buyCentsFromLiveAsk(live, impliedProb) {
  const ask = Number(live?.askCents);
  if (Number.isFinite(ask) && ask > 0) return ask;
  const p = toProb(impliedProb);
  return Number.isFinite(p) ? p * 100 : null;
}

/** Scale Yes probs when exclusive-set overflows; else null (caller keeps inputs). */
export function scaleExclusiveYesIfOverflow(yesProbs) {
  const list = (Array.isArray(yesProbs) ? yesProbs : []).map((p) => {
    const n = toProb(p);
    return Number.isFinite(n) ? n : DEFAULT_HALF;
  });
  if (list.length < 2) return null;
  const sum = list.reduce((s, p) => s + p, 0);
  if (!exclusiveSumOverflow(sum)) return null;
  return redistributeExclusive(
    list,
    list.map((p) => !looksLikeDefaultHalf(p))
  );
}

/**
 * N-way outcome implied probs (0–1), exclusive-normalized.
 * @param {number[]} rawPrices
 * @param {{ anchored?: boolean[] }} [opts]
 */
export function normalizeMultiOutcomeProbs(rawPrices, opts = {}) {
  const probs = (Array.isArray(rawPrices) ? rawPrices : []).map((p) => {
    const n = toProb(p);
    return Number.isFinite(n) ? n : DEFAULT_HALF;
  });
  if (probs.length < 3) return probs;
  const sum = probs.reduce((s, p) => s + p, 0);
  // Only rescale overflow; leave valid ~100% PM sets untouched.
  if (!exclusiveSumOverflow(sum)) return probs;
  return redistributeExclusive(probs, opts.anchored);
}
